export enum NotificationStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  CANCELLED = 'CANCELLED',
}

export enum NotificationSeverity {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum NotificationThreadType {
  OPERATION = 'operation',
  RESOURCE = 'resource',
  ANNOUNCEMENT = 'announcement',
  SYSTEM = 'system',
}

export enum NotificationSourceType {
  WORKFLOW = 'workflow',
  RESOURCE = 'resource',
  ANNOUNCEMENT = 'announcement',
  SYSTEM = 'system',
}

/**
 * 서버 분류 카테고리.
 * @description
 * - operation: 사용자 요청 작업 결과
 * - resource_lifecycle: 리소스 lifecycle 이벤트
 * - platform_notice: 콘솔 공지/새소식
 */
export enum NotificationCategory {
  OPERATION = 'operation',
  RESOURCE_LIFECYCLE = 'resource_lifecycle',
  PLATFORM_NOTICE = 'platform_notice',
}

export interface NotificationResource {
  error_message: string | null;
  error_type: string | null;
  id: string;
  link: string | null;
  name: string;
  status: NotificationResourceStatus;
}

/**
 * 개별 리소스 실행 결과 상태 — SUCCESS / ERROR 2개만 내려온다.
 */
export type NotificationResourceStatus = NotificationStatus.SUCCESS | NotificationStatus.ERROR;

/**
 * Grouped Notification에 포함된 개별 item.
 * @description
 * - swagger: ProjectNotificationItemModel / NotificationItemModel
 * - `read`는 personal scope에서만 내려옴.
 */
export interface NotificationItem {
  category: NotificationCategory;
  createdAt: string;
  id: string;
  occurredAt: string;
  projectId: string;
  severity: NotificationSeverity;
  sourceType: NotificationSourceType;
  status: NotificationStatus;
  threadType: NotificationThreadType;
  updatedAt: string;
  actorUserId?: string | null;
  actorUserName?: string | null;
  correlationKey?: string | null;
  description?: string;
  domainId?: string | null;

  link?: string | null;
  /** personal scope 전용 */
  read?: boolean;
  requestId?: string | null;
  resourceId?: string | null;
  resources?: NotificationResource[];
  resourceType?: string | null;
  sourceStatus?: string | null;
  summary?: Record<string, unknown>;
  title?: string;
}

/**
 * Notification (그룹) 단위.
 * @description
 * - swagger: ProjectNotificationModel / NotificationModel
 * - `groupedItems`: 같은 thread에 묶인 하위 item 목록 (feed에 함께 포함됨).
 * - `read`/`partialRead`/`readItemCount`/`unreadItemCount`는 personal scope에서만 내려옴.
 */
export interface Notification {
  actorUserId: string | null;
  actorUserName: string | null;
  category: NotificationCategory;
  correlationKey: string;
  createdAt: string;
  description: string;
  domainId: string | null;
  groupCount: number;
  groupedItems: NotificationItem[];
  groupWindowEndedAt: string;
  groupWindowStartedAt: string;
  id: string;
  link: string | null;
  occurredAt: string;
  /** personal scope 전용 — 그룹 내 일부만 읽음 여부. */
  partialRead: boolean;
  projectId: string;
  /** personal scope 전용 — 그룹 전체 읽음 여부. */
  read: boolean;
  /** personal scope 전용 — 그룹 내 읽은 item 수. */
  readItemCount: number;
  requestId: string | null;
  resolvedAt: string;
  resourceId: string | null;
  resources: NotificationResource[];
  resourceType: string | null;
  severity: NotificationSeverity;
  sourceStatus: string | null;
  sourceType: NotificationSourceType;
  status: NotificationStatus;
  summary: Record<string, unknown>;
  threadType: NotificationThreadType;
  title: string;
  /** personal scope 전용 — 그룹 내 안 읽은 item 수. */
  unreadItemCount: number;
  updatedAt: string;
}

/**
 * GNB bell 배지 정보.
 * @description
 * - swagger: NotificationBadgeModel
 * - 사용자가 종을 마지막으로 본 시점(`lastSeenAt`) 이후로 도착한 알림 개수.
 */
export interface NotificationBadge {
  unseenCount: number;
  lastSeenAt?: string | null;
  latestOccurredAt?: string | null;
}

export interface GetNotificationFeedParams {
  category?: NotificationCategory;
  cursor?: string;
  limit?: number;
}

export interface GetNotificationFeedResponse {
  data: Notification[];
  hasMore: boolean;
  limit: number;
  nextCursor: string | null;
}
